// React import`
import { useEffect, useState, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';

// Generic imports
import axios from 'axios';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import CurrencyList from 'currency-list';
import queryString from 'query-string';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { blue, green, red } from '@mui/material/colors';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faPlus, faLayerPlus, faRotateLeft, faBarsStaggered, faAngleUp, faAngleDown, faClone, faBarcodeRead } from '@fortawesome/pro-light-svg-icons';
import { faPenToSquare, faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

// Local imports
import { formFieldPostfix } from '../../FormFieldPostfix';
import Header from '../../Header';
import { migrateFormFromPriceQuoteToOpportunity } from '../../utils/form.js';
import CurrencyFormulaTextField from '../../dashboard/components/CurrencyFormulaTextField.js';
import { formulaToReact, formulaToParams } from '../../utils/Formula.js';
import INCOTERMS from '../../utils/Incoterms.js';
import { FieldTypes } from '../../FieldTypes.js';
import PRODUCT_TYPES from '../../ProductTypes.js';
import OpportunityCargowiseCodesModal from '../opportunities/OpportunityCargowiseCodesModal.js';

const TRANSPORT_MODES = ['air', 'sea', 'land'];

// Product types not offered as a default-cost allocation target: the lowercase
// 'Air freight' singleton (we keep 'Air Freight') and the sea by-dimension/volume
// singletons.
const EXCLUDED_COST_PRODUCT_TYPES = ['Air freight', 'By Dimensions or Weight (sea)', 'By Volume or Weight (sea)'];

export default function OpportunityTemplatesBuilder(props) {
    const { type } = props;
    const { org, accesstoken, onOrgUpdated, suppliers } = useOutletContext();
    const navigate = useNavigate();
    const { id } = useParams();
    const [ form, setForm ] = useState(null);
    const [ modal, setModal ] = useState(null);
    const [ reorderModal, setReorderModal ] = useState(null);
    const [ editing, setEditing ] = useState(null);
    const [ undoList, setUndoList ] = useState([]);
    const [ errors, setErrors ] = useState([]);
    const [ saving, setSaving ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(null);
    const [ cargowiseCodesModal, setCargowiseCodesModal ] = useState(null);

    if ((form) && (!form.transportationMethods)) {
        if (form.transportationMethod) form.transportationMethods = [form.transportationMethod]; else form.transportationMethods = [];
    }

    useEffect(() => {
        if (!org.forms[id]) {
            const parsed = queryString.parse(window.location.search);
            if (parsed.clone) {
                let f = _.cloneDeep(org.forms[parsed.clone]);
                if (f.type === 'price-quote' || f.type === 'supplier') {
                    f = migrateFormFromPriceQuoteToOpportunity({ form: f });
                }
                setForm(f);
            } else {
                setForm({ 
                    title: '', 
                    description: '', 
                    type, 
                    transportationMethods: org.defaults?.transportationMethod ? [org.defaults?.transportationMethod] : [],
                    sections: [ { title: '', description: '', fields: [], defaultCurrency: org.defaults?.currency } ],
                    displayCurrencySource: 'live',
                    displayCurrency: org.defaults?.currency
                });
            }
        } else {
            let f = _.cloneDeep(org.forms[id]);
            if (f.type === 'price-quote' || f.type === 'supplier') {
                f = migrateFormFromPriceQuoteToOpportunity({ form: f });
            }
            setForm(f);
        }
    }, [id, org.forms])

    function setField(field, value) {
        setUndoList(u => [...u, _.cloneDeep(form)]);
        setForm(f => ({ ...f, [field]: value }));
    }

    function onDeleteSection({ sectionIndex }) {
        const s = _.cloneDeep(form.sections);
        _.pullAt(s, sectionIndex);
        setField('sections', s);
    }

    function onDeleteField({ sectionIndex, fieldIndex }) {
        const s = _.cloneDeep(form.sections);
        _.pullAt(s[sectionIndex].fields, fieldIndex);
        setField('sections', s);
    }

    function onAddField({ sectionIndex, fieldIndex = 0 }) {
        const s = _.cloneDeep(form.sections);
        s[sectionIndex].fields.splice(fieldIndex + 1, 0, { id: uuidv4(), title: '', type: 'per-unit' });
        setField('sections', s);
        _.defer(() => setEditing(`section-${sectionIndex}-${fieldIndex + 1}`));
    }

    function onAddSection({ sectionIndex, fieldIndex = 0 }) {
        const s = _.cloneDeep(form.sections);
        const fields = s[sectionIndex].fields.splice(fieldIndex + 1);
        s.splice(sectionIndex + 1, 0, { title: '', description: '', defaultCurrency: org.defaults?.currency, fields: fields || [] });
        setField('sections', s);
        _.defer(() => setEditing(`section-${sectionIndex + 1}`));
    }

    function onCloneSection({ sectionIndex, fieldIndex = 0 }) {
        const s = _.cloneDeep(form.sections);
        // The copy's fields get new ids: two fields sharing an id are taken for one charge when a
        // quote is made from the template. Formulas inside the copy follow the new ids.
        let newSection = _.cloneDeep({ ...s[sectionIndex], title: `Copy of ${s[sectionIndex].title}` });
        const idMap = _.fromPairs((newSection.fields || []).filter(f => f.id).map(f => [f.id, uuidv4()]));
        let json = JSON.stringify(newSection);
        _.forEach(idMap, (to, from) => { json = json.split(from).join(to).split(from.replace(/-/g, '_')).join(to.replace(/-/g, '_')); });
        newSection = JSON.parse(json);
        s.splice(sectionIndex + 1, 0, newSection);
        setField('sections', s);
        _.defer(() => setEditing(`section-${sectionIndex + 1}`));
    }

    function undo() {
        const u = _.clone(undoList);
        const f = u.pop();
        setUndoList(u);
        setForm(f);
    }

    function onReorderSections() {
        setReorderModal({
            options: form.sections,
            onSave: (v) => {
                setEditing(null);
                setField('sections', v);
            }
        });
    }

    function onReorderFields({ sectionIndex }) {
        setReorderModal({
            options: form.sections[sectionIndex].fields,
            onSave: (v) => {
                const s = _.cloneDeep(form.sections);
                s[sectionIndex].fields = v;
                setEditing(null);
                setField('sections', s);
            }
        });
    }

    // Convert form structure to offer structure for cargowise codes modal
    function formToOfferStructure(formData) {
        return {
            sections: formData.sections.map(section => ({
                title: section.title,
                offers: [{
                    title: '',
                    fields: section.fields.map(field => ({
                        title: field.title,
                        cargowiseChargeCode: field.cargowiseChargeCode || ''
                    }))
                }]
            }))
        };
    }

    // Convert offer structure back to form structure
    function offerToFormStructure(offerData, originalForm) {
        const updatedForm = _.cloneDeep(originalForm);
        offerData.sections.forEach((section, sectionIndex) => {
            if (section.offers && section.offers[0]) {
                section.offers[0].fields.forEach((field, fieldIndex) => {
                    if (updatedForm.sections[sectionIndex] && updatedForm.sections[sectionIndex].fields[fieldIndex]) {
                        if (field.cargowiseChargeCode) {
                            updatedForm.sections[sectionIndex].fields[fieldIndex].cargowiseChargeCode = field.cargowiseChargeCode;
                        } else {
                            delete updatedForm.sections[sectionIndex].fields[fieldIndex].cargowiseChargeCode;
                        }
                    }
                });
            }
        });
        return updatedForm;
    }

    function onOpenCargowiseCodes() {
        const offerStructure = formToOfferStructure(form);
        setCargowiseCodesModal(offerStructure);
    }

    async function onSave() {
        const e = [];

        if (!form.title) e.push('Form does not have a title.');
        if (!form.type) e.push('Form type not selected (rate or opportunity).');
        if (form.transportationMethods.length === 0) e.push('Form need to be applied to at least one transportation method (air, sea, or land).');

        form.sections.forEach((section, sectionIndex) => {
            if (!section.title) e.push(`Section #${sectionIndex + 1} does not have a title.`);
            section.fields.forEach((field, fieldIndex) => {
                if (!field.title) e.push(`In section #${sectionIndex + 1}, field #${fieldIndex + 1} does not have a title.`);
                if (field.default?.formula && formulaToParams({ formula: field.default.formula, sections: form.sections })?.tax && field.type !== 'flat') e.push(`In section #${sectionIndex + 1}, field #${fieldIndex + 1} has a tax formula but is not priced per shipment.`);
                if (field.default?.formula && field.type === 'custom') e.push(`In section #${sectionIndex + 1}, field #${fieldIndex + 1} has a formula but is custom priced (unsupported).`);
                if (_.some(form.transportationMethods, tm => !_.some(FieldTypes[tm], ft => ft.value === field.type))) e.push(`In section #${sectionIndex + 1}, field #${fieldIndex + 1} has an unsupported field type.`);
            });
        });

        if (e.length > 0) {
            setSaving(true);
            await new Promise(res => setTimeout(res, 500));
            setSaving(false);
            setErrors(e);
            setSavingSuccess('no');
            await new Promise(res => setTimeout(res, 3000));
            setSavingSuccess(null);
            return;
        }
        setErrors(e);

        setSaving(true);

        try {
            const f = _.cloneDeep(org.forms);
            if (f[id] && f[id].type !== 'supplier' && f[id].type !== 'price-quote') {
                await axios.post(`/api/organizations/${org.id}/forms/patch/${id}?accesstoken=${accesstoken}`, { deleted: true });
                f[id].deleted = true;
            }
            const { data: newId } = await axios.post(`/api/organizations/${org.id}/forms?accesstoken=${accesstoken}`, form);
            f[newId] = _.cloneDeep(form);
            f[newId].deleted = false;
            onOrgUpdated({ ...org, forms: f });
            setSaving(false);
            setSavingSuccess('yes');
            navigate(`../forms/${newId}`, { replace: true });
        } catch(e) {
            setSaving(false);
            setSavingSuccess('no');
            setErrors([e.message]);
            console.log('Error saving:', e);
        }

        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    const currencyOptions = useMemo(() => {
        return Object.keys(CurrencyList.getAll('en_US')).map(currency => currency);
    }, []);

    const productTypeOptions = useMemo(() => {
        return _(form?.transportationMethods || [])
            .flatMap(tm => (PRODUCT_TYPES[tm] || []).map(p => p.name))
            .uniq()
            .reject(name => EXCLUDED_COST_PRODUCT_TYPES.includes(name))
            .value();
    }, [form?.transportationMethods]);

    // Cost calculation methods = the field types valid for buying rates across the
    // form's transport modes (same set as the sell-rate "Field is:" selector, but
    // filtered with forBuyingRate: true).
    const costCalcMethodOptions = useMemo(() => {
        const tms = form?.transportationMethods || [];
        if (tms.length === 0) return [];
        // Same filter the manual buying-rate UI uses: aggregated types are hidden for
        // non-air modes, enabled() is consulted with forBuyingRate, and custom/string are
        // excluded. For sea/land this leaves "per type" + "shipment".
        const allowedPerMode = tms.map(tm => (FieldTypes[tm] || []).filter(ft => {
            const modeOk = tm === 'air' || !ft.isAggregated;
            const enabledOk = !_.isFunction(ft.enabled) || ft.enabled({ organization: org, forBuyingRate: true });
            const notSpecial = ft.value !== 'custom' && ft.value !== 'string' && ft.value !== 'date';
            return modeOk && enabledOk && notSpecial;
        }));
        return _.intersectionBy(...allowedPerMode, ft => ft.value).map(ft => ({
            value: ft.value,
            label: _(tms)
                .map(tm => { const a = FieldTypes[tm].find(t => t.value === ft.value); return a ? { tm, label: a.label } : null; })
                .compact()
                .groupBy('label')
                .map((group, label) => (tms.length === 1 || group.length === tms.length) ? label : `${label} (${group.map(a => a.tm).join(', ')})`)
                .value()
                .join(' / '),
        }));
    }, [form?.transportationMethods, org]);

    if (!form) {
        return null;
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faWpforms}
                title={`${type === 'rate' ? 'Supplier & Rate Forms' : 'Opportunity Templates'} - ${org.forms[id] ? 'Edit Form' : 'New Form'}`}
                buttons={[{
                    title: 'Undo',
                    icon: faRotateLeft,
                    color: 'monada-button',
                    action: undo,
                    disabled: undoList.length === 0,
                }, {
                    title: 'Save',
                    icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : null,
                    iconColor: savingSuccess === 'yes' ? green[500] : savingSuccess === 'no' ? red[500] : null,
                    width: '80px',
                    color: 'monada-button',
                    action: onSave,
                    thinking: saving,
                    disabled: saving || !!savingSuccess,
                }]}
                onBack={() => navigate(-1)}
            />
            {errors.length > 0 && (<Alert severity='error' sx={{ marginBottom: '12px' }}>
                    <div>
                        Sorry, there are some errors in the form. Please fix them before saving:<br/>
                    </div>
                    <ul>
                        {errors.map(e => (
                            <li key={e}>{e}</li>
                        ))}
                    </ul>
                </Alert>
            )}
            <Paper 
                onClick={() => editing !== 'form' ? setEditing('form') : ''}
                elevation={1} 
                sx={{ 
                    maxWidth: '70%', width: '70%', margin: 'auto',
                    padding: '24px', display: 'flex', flexDirection: 'column',
                    borderLeft: editing === 'form' ? '3px solid #605de4' : '' }}
            >
                <Box sx={{ display: 'flex' }}>
                    <Typography variant='h4'>
                        {form.title || 'Untitled Form'}
                    </Typography>
                    {editing === 'form' && (
                        <Button onClick={() => setModal({ field: 'title', previous: form.title })}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex' }}>
                    <Typography variant='body1'>
                        {form.description || 'Form description'}
                    </Typography>
                    {editing === 'form' && (
                        <Button onClick={() => setModal({ field: 'description', previous: form.description })}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                        </Button>
                    )}
                </Box>

                {editing === 'form' && (
                    <FormControl sx={{ marginTop: '24px' }}>
                        <FormLabel>During:</FormLabel>
                        <FormGroup row>
                            {TRANSPORT_MODES.map(transportMode => (
                                <FormControlLabel key={transportMode}
                                    control={
                                        <Checkbox
                                            disabled={form.transportationMethods.length === 1 && form.transportationMethods[0] === transportMode}
                                            size={'small'}
                                            checked={form.transportationMethods.includes(transportMode)}
                                            onChange={(e, t) => { 
                                                setField('transportationMethods', t ? [...form.transportationMethods, transportMode] : _.without(form.transportationMethods, transportMode));
                                            }}
                                        />}
                                    label={`${_.startCase(transportMode)} Transport`}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>
                )}
                {editing === 'form' && (
                    <FormControl sx={{ marginTop: '24px' }}>
                        <FormLabel>Incoterm:</FormLabel>
                        <Typography variant='caption' >The seven rules for any mode(s) of transport</Typography>
                        <FormGroup row>
                            {INCOTERMS.filter(i => i.group === 1).map(incoterm => (
                                <FormControlLabel key={incoterm.value}
                                    control={
                                        <Checkbox
                                            size={'small'}
                                            checked={(form.incoterms || []).includes(incoterm.value)}
                                            onChange={(e, t) => {
                                                setField('incoterms', t ? [...(form.incoterms || []), incoterm.value] : _.without((form.incoterms || []), incoterm.value));
                                            }}
                                        />}
                                    label={incoterm.text}
                                />
                            ))}
                        </FormGroup>
                        <Typography variant='caption' >The four rules for Sea and Inland Waterway Transport</Typography>
                        <FormGroup row>
                            {INCOTERMS.filter(i => i.group === 2).map(incoterm => (
                                <FormControlLabel key={incoterm.value}
                                    control={
                                        <Checkbox
                                            disabled={!form.transportationMethods.includes('sea') && !form.transportationMethods.includes('land')}
                                            size={'small'}
                                            checked={(form.incoterms || []).includes(incoterm.value)}
                                            onChange={(e, t) => {
                                                setField('incoterms', t ? [...(form.incoterms || []), incoterm.value] : _.without((form.incoterms || []), incoterm.value));
                                            }}
                                        />}
                                    label={incoterm.text}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>
                )}
                {editing === 'form' && (
                    <Box sx={{ marginTop: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                                size='small'
                                checked={form.displayCurrencyEnabled !== false && !!form.displayCurrency}
                                onChange={(e) => {
                                    if (!e.target.checked) {
                                        setUndoList(u => [...u, _.cloneDeep(form)]);
                                        setForm(f => ({ ...f, displayCurrency: null, displayCurrencySource: null, displayCurrencyEnabled: false }));
                                    } else {
                                        setUndoList(u => [...u, _.cloneDeep(form)]);
                                        setForm(f => ({ ...f, displayCurrencyEnabled: true, displayCurrency: f.displayCurrency || org.defaults?.currency, displayCurrencySource: f.displayCurrencySource || 'live' }));
                                    }
                                }}
                            />
                            <Typography variant='body2'>Enable currency conversion on display</Typography>
                        </Box>
                        <Typography sx={{ marginTop: '4px' }} variant='caption'>(Optional) This is the currency that will be used to display the offer total.</Typography>
                    </Box>
                )}
                {editing === 'form' && form.displayCurrencyEnabled !== false && (form.displayCurrency || form.displayCurrencyEnabled === true) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: '12px' }}>
                        <FormControl sx={{ flex: 1 }}>
                            <InputLabel>Display Currency Source</InputLabel>
                            <Select
                                label='Display Currency Source'
                                value={form.displayCurrencySource || (form.displayCurrency ? 'live' : 'none')}
                                onChange={(event) => {
                                    const v = event.target.value;
                                    if (v === 'none') {
                                        setUndoList(u => [...u, _.cloneDeep(form)]);
                                        setForm(f => ({ ...f, displayCurrency: null, displayCurrencySource: null, displayCurrencyEnabled: false }));
                                    } else {
                                        setField('displayCurrencySource', v);
                                    }
                                }}
                            >
                                <MenuItem value='none'>No conversion</MenuItem>
                                <MenuItem value='live'>Always use live rates</MenuItem>
                                <MenuItem value='fixed'>Prefer fixed rates, fallback to live rates</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <Autocomplete
                                options={currencyOptions}
                                getOptionLabel={option => `${CurrencyList.getAll('en_US')[option].name} (${option}, ${CurrencyList.getAll('en_US')[option].symbol})`}
                                renderInput={(params) => <TextField label='Display Currency' {...params} />}
                                value={form.displayCurrency}
                                onChange={(e, value) => {
                                    setField('displayCurrency', value);
                                    setField('displayCurrencySource', form.displayCurrencySource || 'live');
                                }}
                            />
                        </FormControl>
                    </Box>
                )}
                {editing !== 'form' && (
                    <Typography variant='body1' sx={{ marginTop: '12px' }}>
                        This form is used for <b>{form.transportationMethods.join(', ')}</b> transport.
                    </Typography>
                )}
                {editing !== 'form' && !_.isEmpty(form.incoterms) && (
                    <Typography variant='body1' sx={{ marginTop: '12px' }}>
                        This form is used for <b>{form.incoterms.map(i => INCOTERMS.find(ic => ic.value === i)?.text).join(', ')}</b> incoterms.
                    </Typography>
                )}
                {editing !== 'form' && form.displayCurrency && (
                    <Typography variant='body1' sx={{ marginTop: '12px' }}>
                        This offer total will display in <b>{CurrencyList.get(form.displayCurrency).name} ({CurrencyList.get(form.displayCurrency).symbol})</b>.
                    </Typography>
                )}
            </Paper>
            {form.sections.map((section, index) => {
                const editingThis = editing === `section-${index}`;
                return (
                    <Box
                        sx={{ maxWidth: '70%', width: '70%', margin: 'auto', marginTop: '12px', position: 'relative' }}
                    >
                        <Box sx={{ background: '#605de4', color: 'white', maxWidth: '200px', padding: '6px 12px', borderRadius: '4px 4px 0px 0px', display: 'inline-block' }}>
                            <Typography variant='caption' sx={{ fontWeight: 800 }}>
                                Section {index + 1} of {form.sections.length}
                            </Typography>
                        </Box>
                        <Paper elevation={3} 
                            onClick={() => !editingThis ? setEditing(`section-${index}`) : null}
                            sx={{ 
                                padding: '24px', display: 'flex', flexDirection: 'column',
                                borderTop: '12px solid #605de4', width: '100%', borderRadius: '0px 4px 4px 4px',
                                borderLeft: editingThis ? '3px solid #605de4' : ''
                            }}
                        >
                            <Box sx={{ display: 'flex' }}>
                                <Typography variant='h5'>
                                    {section.title || 'Untitled Section'}
                                </Typography>
                                {editingThis && (
                                    <Button onClick={() => setModal({ parent: 'section', sectionIndex: index, field: 'title', previous: section.title })}>
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                    </Button>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex' }}>
                                <Typography variant='body1'>
                                    {section.description || 'Section description'}
                                </Typography>
                                {editingThis && (
                                    <Button onClick={() => setModal({ parent: 'section', sectionIndex: index, field: 'description', previous: section.description })}>
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                    </Button>
                                )}
                            </Box>

                            {editingThis && (
                                <FormControl sx={{ marginTop: '24px' }}>
                                    <FormLabel>Prices in this section are normally in:</FormLabel>
                                    <Box>
                                        <Select
                                            value={section.defaultCurrency}
                                            onChange={(event) => {
                                                const _s = _.cloneDeep(form.sections);
                                                _s[index].defaultCurrency = event.target.value;
                                                setField('sections', _s)
                                            }}
                                        >
                                            {_.values(CurrencyList.getAll()['en']).map(c => (
                                                <MenuItem key={c.code} value={c.code}>{c.name} ({c.symbol})</MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </FormControl>
                            )}
                            {editingThis && (
                                <Typography variant='body1' sx={{ marginTop: '12px' }}>
                                    <FormLabel id='form-type-group-label'>Display Currency (optional, section total will be converted to this currency):</FormLabel>
                                </Typography>
                            )}
                            {editingThis && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: '12px' }}>
                                    <FormControl sx={{ flex: 1}}>
                                        <InputLabel>Display Currency Source</InputLabel>
                                        <Select
                                            label='Display Currency Source'
                                            value={section.displayCurrencySource || (section.displayCurrency ? 'live' : 'none')}
                                            onChange={(event) => {
                                                const v = event.target.value;
                                                const _s = _.cloneDeep(form.sections);
                                                if (v === 'none') {
                                                    _s[index].displayCurrency = null;
                                                    _s[index].displayCurrencySource = null;
                                                } else {
                                                    _s[index].displayCurrencySource = v;
                                                }
                                                setField('sections', _s)
                                            }}
                                        >
                                            <MenuItem value='none'>No conversion</MenuItem>
                                            <MenuItem value='live'>Always use live rates</MenuItem>
                                            <MenuItem value='fixed'>Prefer fixed rates, fallback to live rates</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ flex: 1 }}l>
                                        <Autocomplete
                                            options={currencyOptions}
                                            getOptionLabel={option => `${CurrencyList.getAll('en_US')[option].name} (${option}, ${CurrencyList.getAll('en_US')[option].symbol})`}
                                            renderInput={(params) => <TextField label='Display Currency' {...params} />}
                                            value={section.displayCurrency}
                                            onChange={(e, value) => {
                                                const _s = _.cloneDeep(form.sections);
                                                _s[index].displayCurrency = value;
                                                _s[index].displayCurrencySource = _s[index].displayCurrencySource || 'live';
                                                setField('sections', _s)
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            )}
                            {!editingThis && (
                                <Typography variant='body1' sx={{ marginTop: '12px' }}>
                                    This section&apos;s fields are calculated in <b>{CurrencyList.get(section.defaultCurrency || org.defaults?.currency || 'USD').name} ({CurrencyList.get(section.defaultCurrency || org.defaults?.currency || 'USD').symbol})</b>.
                                    {section.displayCurrency ? <span> The section total will display in <b>{CurrencyList.get(section.displayCurrency || org.defaults?.currency || 'USD').name} ({CurrencyList.get(section.displayCurrency || org.defaults?.currency || 'USD').symbol})</b>.</span> : null}
                                </Typography>
                            )}
                        </Paper>

                        {section.fields.map((field, fieldIndex) => {
                            const editingThis = editing === `section-${index}-${fieldIndex}`;
                            const costCalcDefault = costCalcMethodOptions.some(o => o.value === field.type) ? field.type
                                : (costCalcMethodOptions.some(o => o.value === 'flat') ? 'flat' : (costCalcMethodOptions[0]?.value || ''));
                            const costCurrency = field.defaultCost?.currency || field.default?.currency || section.defaultCurrency || org.defaults?.currency;
                            return (
                                <Box sx={{ position: 'relative' }}>
                                    <Paper
                                        sx={{ 
                                            padding: '24px', display: 'flex', flexDirection: 'column',
                                            width: '100%', borderRadius: '0px 4px 4px 4px', marginTop: '12px',
                                            borderLeft: editingThis ? '3px solid #605de4' : ''
                                        }}
                                        onClick={() => setEditing(`section-${index}-${fieldIndex}`)}
                                    >
                                        <Box sx={{ display: 'flex' }}>
                                            <Typography variant='h6'>
                                                {field.title || 'Untitled Field'}
                                            </Typography>
                                            {editingThis && (
                                                <Button onClick={() => setModal({ parent: 'field', fieldIndex, sectionIndex: index, field: 'title', previous: field.title })}>
                                                    <FontAwesomeIcon icon={faPenToSquare} />
                                                </Button>
                                            )}
                                        </Box>
                                        {editingThis && (
                                            <FormControl sx={{ marginTop: '24px' }}>
                                                <FormLabel id='link-type-group-label'>Field is:</FormLabel>
                                                <Select
                                                    value={field.type}
                                                    onChange={(event) => {
                                                        const _s = _.cloneDeep(form.sections);
                                                        _s[index].fields[fieldIndex].type = event.target.value;
                                                        setField('sections', _s)
                                                    }}
                                                >
                                                    {_(form.transportationMethods)
                                                        .map(t => FieldTypes[t])
                                                        .thru(arrs => _.intersectionBy(...arrs, t => t.value))
                                                        .filter(ft => !_.isFunction(ft.enabled) || ft.enabled({ organization: org, forBuyingRate: false }))
                                                        .map(ft => (
                                                            <MenuItem 
                                                                key={ft.value} 
                                                                value={ft.value}
                                                            >
                                                                {_(form.transportationMethods)
                                                                    .map(tm => {
                                                                        const a = FieldTypes[tm].find(t => t.value === ft.value)
                                                                        if (!a) return null;
                                                                        return { tm: tm, label: a.label };
                                                                    })
                                                                    .compact()
                                                                    .groupBy('label')
                                                                    .map((group, label) => {
                                                                        if (form.transportationMethods?.length === 1) {
                                                                            return label;
                                                                        }
                                                                        if (group.length === form.transportationMethods?.length) {
                                                                            return label;
                                                                        }
                                                                        return `${label} (${group.map(a => a.tm).join(', ')})`;
                                                                    })
                                                                    .value()
                                                                    .join(' / ')}
                                                            </MenuItem>
                                                        )).value()
                                                    }
                                                    <MenuItem value={'date'}>Date</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                        {editingThis && field.type !== 'string' && field.type !== 'date' && (
                                            <FormControl sx={{ marginTop: '24px' }}>
                                                <FormLabel>Currency:</FormLabel>
                                                <Box>
                                                    <Select
                                                        value={field.currency || '-'}
                                                        onChange={(event) => {
                                                            const _s = _.cloneDeep(form.sections);
                                                            if (event.target.value === '-') {
                                                                delete _s[index].fields[fieldIndex].currency;
                                                            } else {
                                                                _s[index].fields[fieldIndex].currency = event.target.value;
                                                            }
                                                            setField('sections', _s)
                                                        }}
                                                    >
                                                        <MenuItem value={'-'}>(By Section)</MenuItem>
                                                        {_.values(CurrencyList.getAll()['en']).map(c => (
                                                            <MenuItem key={c.code} value={c.code}>{c.name} ({c.symbol})</MenuItem>
                                                        ))}
                                                    </Select>
                                                </Box>
                                            </FormControl>
                                        )}
                                        {editingThis && field.type !== 'string' && field.type !== 'date' && (
                                            <FormControl sx={{ marginTop: '24px' }}>
                                                <FormLabel id='form-type-group-label'>Default Sell Value:</FormLabel>
                                                <CurrencyFormulaTextField
                                                    value={field.default?.value}
                                                    currency={field.default?.currency || section.defaultCurrency}
                                                    formula={field.default?.formula}
                                                    setValue={(v) => {
                                                        const _s = _.cloneDeep(form.sections);
                                                        if (v === '') {
                                                            delete _s[index].fields[fieldIndex].default;
                                                        } else {
                                                            const vv = parseFloatOrString(v);
                                                            _s[index].fields[fieldIndex].default = _s[index].fields[fieldIndex].default || {};
                                                            _s[index].fields[fieldIndex].default = { value: vv, currency: field.default?.currency || section.defaultCurrency, formula: field.default?.formula };
                                                        }
                                                        setField('sections', _s)
                                                    }}
                                                    setCurrency={(v) => {
                                                        const _s = _.cloneDeep(form.sections);
                                                        if (v === '-') {
                                                            delete _s[index].fields[fieldIndex].default.currency;
                                                        } else {
                                                            _s[index].fields[fieldIndex].default = _s[index].fields[fieldIndex].default || {};
                                                            _s[index].fields[fieldIndex].default.currency = v;
                                                        }
                                                        setField('sections', _s)
                                                    }}
                                                    setFormula={(v) => {
                                                        const _s = _.cloneDeep(form.sections);
                                                        if (v === '') {
                                                            delete _s[index].fields[fieldIndex].default.formula;
                                                        } else {
                                                            _s[index].fields[fieldIndex].default = _s[index].fields[fieldIndex].default || {};
                                                            _s[index].fields[fieldIndex].default.formula = v;
                                                        }
                                                        setField('sections', _s)
                                                    }}
                                                    focused={'default'} 
                                                    focusedId={'default'}
                                                    onBlur={() => {}}
                                                    onCancelBlur={() => {}}
                                                    disabled={false}
                                                    width='150px'
                                                    sections={form.sections}
                                                    organization={org}
                                                />
                                                <Box sx={{ fontSize: '13px' }}>
                                                    {field.default?.formula
                                                        ? formulaToReact({
                                                            formula: field.default.formula,
                                                            currency: field.default.currency || section.defaultCurrency,
                                                            multiplierText: 'item',
                                                            sections: form.sections,
                                                            errorValue: null
                                                        }) : null}
                                                    {field.default?.formula &&
                                                            formulaToParams({ formula: field.default.formula, sections: form.sections })?.tax && 
                                                            field.type !== 'flat' && (
                                                        <Alert severity={'error'}>
                                                            Tax formulas can only be used in fields that are priced per shipment.
                                                        </Alert>
                                                    )}
                                                    {field.default?.formula &&
                                                            !formulaToParams({ formula: field.default.formula, sections: form.sections })?.tax && 
                                                            field.type === 'custom' && (
                                                        <Alert severity={'error'}>
                                                            Formulas cannot be used in fields that are custom priced.
                                                        </Alert>
                                                    )}
                                                </Box>
                                            </FormControl>
                                        )}
                                        {editingThis && field.type === 'string' && (
                                            <FormControl sx={{ marginTop: '24px' }}>
                                                <FormLabel id='form-type-group-label'>Default value (optional):</FormLabel>
                                                <TextField
                                                    value={field.default?.value || ''}
                                                    onChange={(e) => {
                                                        const _s = _.cloneDeep(form.sections);
                                                        const v = e.target.value;
                                                        if (v === '') {
                                                            delete _s[index].fields[fieldIndex].default;
                                                        } else {
                                                            _s[index].fields[fieldIndex].default = _s[index].fields[fieldIndex].default || {};
                                                            _s[index].fields[fieldIndex].default = { value: v };
                                                        }
                                                        setField('sections', _s)
                                                    }}
                                                />
                                            </FormControl>
                                        )}
                                        {editingThis && (
                                            <Typography variant='body1' sx={{ marginTop: '12px' }}>
                                                <FormLabel id='form-type-group-label'>Display Currency (optional, field price will be converted to this currency):</FormLabel>
                                            </Typography>
                                        )}
                                        {editingThis && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: '12px' }}>
                                                <FormControl sx={{ flex: 1 }}>
                                                    <InputLabel>Display Currency Source</InputLabel>
                                                    <Select
                                                        label='Display Currency Source'
                                                        value={field.displayCurrencySource || (field.displayCurrency ? 'live' : 'none')}
                                                        onChange={(event) => {
                                                            const v = event.target.value;
                                                            const _s = _.cloneDeep(form.sections);
                                                            if (v === 'none') {
                                                                delete _s[index].fields[fieldIndex].displayCurrency;
                                                                delete _s[index].fields[fieldIndex].displayCurrencySource;
                                                            } else {
                                                                _s[index].fields[fieldIndex].displayCurrencySource = v;
                                                            }
                                                            setField('sections', _s)
                                                        }}
                                                    >
                                                        <MenuItem value='none'>No conversion</MenuItem>
                                                        <MenuItem value='live'>Always use live rates</MenuItem>
                                                        <MenuItem value='fixed'>Prefer fixed rates, fallback to live rates</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <FormControl sx={{ flex: 1 }} l>
                                                    <Autocomplete
                                                        options={currencyOptions}
                                                        getOptionLabel={option => `${CurrencyList.getAll('en_US')[option].name} (${option}, ${CurrencyList.getAll('en_US')[option].symbol})`}
                                                        renderInput={(params) => <TextField label='Display Currency' {...params} />}
                                                        value={field.displayCurrency}
                                                        onChange={(e, value) => {
                                                            const _s = _.cloneDeep(form.sections);
                                                            if (!value) {
                                                                delete _s[index].fields[fieldIndex].displayCurrency;
                                                            } else {
                                                                _s[index].fields[fieldIndex].displayCurrency = value;
                                                                _s[index].fields[fieldIndex].displayCurrencySource = _s[index].fields[fieldIndex].displayCurrencySource || 'live';
                                                            }
                                                            setField('sections', _s)
                                                        }}
                                                    />
                                                </FormControl>
                                            </Box>
                                        )}
                                        {editingThis && field.type !== 'string' && field.type !== 'date' && (
                                            <Box sx={{ marginTop: '24px', padding: '16px', borderRadius: '8px', backgroundColor: '#f6f7fb', border: '1px solid #e3e6f0' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Switch
                                                        size='small'
                                                        checked={!!field.defaultCost}
                                                        onChange={(e) => {
                                                            const _s = _.cloneDeep(form.sections);
                                                            _s[index].fields[fieldIndex].defaultCost = e.target.checked ? {} : null;
                                                            setField('sections', _s);
                                                        }}
                                                    />
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>Default Cost (optional)</Typography>
                                                </Box>
                                                <Typography variant='caption' component='div' sx={{ opacity: 0.7, marginBottom: '4px' }}>Everything above sets the sell rate. This is the only section that sets the buying cost for this charge.</Typography>
                                                {field.defaultCost && (
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, marginTop: '8px' }}>
                                                    <FormControl sx={{ flex: 1.4 }} error={!!field.defaultCost?.value && !field.defaultCost?.supplier?.organization}>
                                                        <Autocomplete
                                                            options={_(suppliers || []).filter(s => s && s.name && !s.inactive).map(s => s.name).sortBy(n => n.toUpperCase()).value()}
                                                            value={field.defaultCost?.supplier?.organization || null}
                                                            isOptionEqualToValue={(option, value) => option === value}
                                                            autoHighlight
                                                            filterOptions={filterSuppliers}
                                                            renderOption={(props, option) => <Box component='li' {...props} sx={{ fontSize: '0.8rem' }}>{option}</Box>}
                                                            onChange={(e, name) => {
                                                                const _s = _.cloneDeep(form.sections);
                                                                if (!name) {
                                                                    _s[index].fields[fieldIndex].defaultCost = { ...field.defaultCost, supplier: null };
                                                                } else {
                                                                    const sup = (suppliers || []).find(s => s.name === name);
                                                                    const uid = sup?.uniqueId && sup.uniqueId.replace(/:/g, '').trim() ? sup.uniqueId : undefined;
                                                                    const email = sup?.contacts?.[0]?.email || undefined;
                                                                    _s[index].fields[fieldIndex].defaultCost = { ...field.defaultCost, supplier: { organization: name, ...(uid ? { uniqueId: uid } : {}), ...(email ? { email } : {}) } };
                                                                }
                                                                setField('sections', _s);
                                                            }}
                                                            renderInput={(params) => <TextField {...params} label='Supplier' placeholder='Pick Supplier…' error={!!field.defaultCost?.value && !field.defaultCost?.supplier?.organization} />}
                                                        />
                                                    </FormControl>
                                                    <FormControl sx={{ flex: 1 }}>
                                                        <InputLabel id='default-cost-product-label'>Product</InputLabel>
                                                        <Select
                                                            labelId='default-cost-product-label'
                                                            label='Product'
                                                            value={productTypeOptions.includes(field.defaultCost?.product) ? field.defaultCost.product : ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                const _s = _.cloneDeep(form.sections);
                                                                _s[index].fields[fieldIndex].defaultCost = { ...field.defaultCost, ...(v ? { product: v } : { product: undefined }) };
                                                                setField('sections', _s);
                                                            }}
                                                        >
                                                            <MenuItem value=''><em>All products</em></MenuItem>
                                                            {productTypeOptions.map(p => (
                                                                <MenuItem key={p} value={p}>{p}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <FormControl sx={{ flex: 1 }}>
                                                        <InputLabel id='default-cost-calc-label'>Priced per</InputLabel>
                                                        <Select
                                                            labelId='default-cost-calc-label'
                                                            label='Priced per'
                                                            value={field.defaultCost?.calculationMethod || costCalcDefault}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                const _s = _.cloneDeep(form.sections);
                                                                _s[index].fields[fieldIndex].defaultCost = { ...field.defaultCost, calculationMethod: v };
                                                                setField('sections', _s);
                                                            }}
                                                        >
                                                            {costCalcMethodOptions.map(o => (
                                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <TextField
                                                        label='Cost value'
                                                        type='number'
                                                        value={field.defaultCost?.value ?? ''}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            const _s = _.cloneDeep(form.sections);
                                                            if (v === '' || v === null) {
                                                                _s[index].fields[fieldIndex].defaultCost = field.defaultCost ? { ...field.defaultCost, value: null } : null;
                                                            } else {
                                                                const parsed = parseFloatOrString(v);
                                                                _s[index].fields[fieldIndex].defaultCost = { ...field.defaultCost, value: parsed, currency: costCurrency, calculationMethod: field.defaultCost?.calculationMethod || costCalcDefault };
                                                            }
                                                            setField('sections', _s);
                                                        }}
                                                        InputProps={{ endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <Select
                                                                    variant='standard'
                                                                    disableUnderline
                                                                    value={costCurrency}
                                                                    onChange={(e) => {
                                                                        const _s = _.cloneDeep(form.sections);
                                                                        _s[index].fields[fieldIndex].defaultCost = { ...field.defaultCost, currency: e.target.value };
                                                                        setField('sections', _s);
                                                                    }}
                                                                    renderValue={(c) => `${c}${CurrencyList.get(c)?.symbol ? ` (${CurrencyList.get(c).symbol})` : ''}`}
                                                                    sx={{ fontSize: '0.85rem', '& .MuiSelect-select': { paddingY: 0 } }}
                                                                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                                                                >
                                                                    {currencyOptions.map(c => (
                                                                        <MenuItem key={c} value={c}>{c}{CurrencyList.get(c)?.symbol ? ` (${CurrencyList.get(c).symbol})` : ''}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </InputAdornment>
                                                        ) }}
                                                        sx={{ width: '210px' }}
                                                    />
                                                </Box>
                                                )}
                                                {field.defaultCost && !!field.defaultCost?.value && !field.defaultCost?.supplier?.organization && (
                                                    <Alert severity='error' sx={{ marginTop: '4px' }}>A supplier is required when a default cost is set.</Alert>
                                                )}
                                            </Box>
                                        )}
                                        {!editingThis && (
                                            <Typography variant='body1' sx={{ marginTop: '12px' }}>
                                                We expect to receive <b>
                                                    {(() => {
                                                        const t = field.type;
                                                        if (t === 'date') return 'a date';
                                                        if (t === 'string') return 'a text';
                                                        return `a number (${_(form.transportationMethods)
                                                            .map(tm => {
                                                                const a = FieldTypes[tm].find(tt => tt.value === t)
                                                                if (!a) return null;
                                                                return { tm: tm, label: a.label };
                                                            })
                                                            .compact()
                                                            .groupBy('label')
                                                            .map((group, label) => {
                                                                if (form.transportationMethods?.length === 1) {
                                                                    return label;
                                                                }
                                                                if (group.length === form.transportationMethods?.length) {
                                                                    return label;
                                                                }
                                                                return `${label} (${group.map(a => a.tm).join(', ')})`;
                                                            })
                                                            .value()
                                                            .join(' / ')
                                                        })`;
                                                    })()}
                                                </b>{field.type !== 'string' && field.type !== 'date' ? <span> using <b>{formFieldPostfix({ field: { postfix: '[currency]' }, currency: field.currency || section.defaultCurrency })}</b></span> : null}.
                                                {field.displayCurrency ? <span> The field price will always be displayed in <b>{CurrencyList.get(field.displayCurrency).name} ({CurrencyList.get(field.displayCurrency).symbol})</b>.</span> : null}
                                            </Typography>
                                        )}
                                        {editingThis && (
                                            <FormControl sx={{ marginTop: '24px' }}>
                                                <Button
                                                    variant='outlined'
                                                    startIcon={<FontAwesomeIcon icon={faBarcodeRead} />}
                                                    onClick={onOpenCargowiseCodes}
                                                >
                                                    Set up Cargowise Codes
                                                </Button>
                                            </FormControl>
                                        )}
                                        {editingThis && (
                                            <Paper style={{ position: 'absolute', top: '0px', right: '-48px', padding: '6px', display: 'flex', flexDirection: 'column' }}>
                                                <IconButton onClick={() => onAddField({ sectionIndex: index, fieldIndex })}><FontAwesomeIcon icon={faPlus}/></IconButton>
                                                <IconButton onClick={() => onAddSection({ sectionIndex: index, fieldIndex })}><FontAwesomeIcon icon={faLayerPlus} /></IconButton>
                                                <div style={{ borderTop: '1px solid #eee', margin: '3px 0px' }} />
                                                <IconButton onClick={() => onReorderFields({ sectionIndex: index })}><FontAwesomeIcon icon={faBarsStaggered} /></IconButton>
                                                <IconButton onClick={() => onDeleteField({ sectionIndex: index, fieldIndex })}><FontAwesomeIcon icon={faTrashCan} /></IconButton>
                                            </Paper>
                                        )}
                                    </Paper>
                                </Box>
                            );
                        })}
                        <Box sx={{ display: 'flex', alignItems: 'center', margin: '24px 0px', flexDirection: 'column'}}>
                            <Typography sx={{ opacity: 0.5 }} variant='caption'>- End of section {index + 1} -</Typography>
                        </Box>
                        {editingThis && (
                            <Paper style={{ position: 'absolute', top: '46px', right: '-48px', padding: '6px', display: 'flex', flexDirection: 'column' }}>
                                <IconButton onClick={() => onAddField({ sectionIndex: index, fieldIndex: -1 })}><FontAwesomeIcon icon={faPlus}/></IconButton>
                                <IconButton onClick={() => onAddSection({ sectionIndex: index, fieldIndex: -1 })}><FontAwesomeIcon icon={faLayerPlus} /></IconButton>
                                <IconButton onClick={() => onCloneSection({ sectionIndex: index, fieldIndex: -1 })}><FontAwesomeIcon icon={faClone} /></IconButton>
                                <div style={{ borderTop: '1px solid #eee', margin: '3px 0px' }} />
                                <IconButton onClick={() => onReorderSections({})}><FontAwesomeIcon icon={faBarsStaggered} /></IconButton>
                                <IconButton disabled={form.sections.length === 1} onClick={() => onDeleteSection({ sectionIndex: index })}><FontAwesomeIcon icon={faTrashCan} /></IconButton>
                            </Paper>
                        )}
                </Box>
                )
            })}
            <FieldModal modal={modal} setModal={setModal} form={form} setField={setField} />
            <ReorderModal modal={reorderModal} setModal={setReorderModal} onSave={(reorderModal || {}).onSave} />
            <OpportunityCargowiseCodesModal
                offer={cargowiseCodesModal}
                onCancel={() => setCargowiseCodesModal(null)}
                onDone={offer => {
                    const updatedForm = offerToFormStructure(offer, form);
                    setField('sections', updatedForm.sections);
                    setCargowiseCodesModal(null);
                }}
            />
        </Box>
    );
}

function FieldModal(props) {
    const { modal, setModal, form, setField } = props;
    const [ value, setValue ] = useState('');

    const _modal = modal || {};


    useEffect(() => {
        setValue(modal ? modal.previous : '');
    }, [modal])

    function save() {
        if (!_modal.parent) {
            setField(_modal.field, value);
        } else {
            const _sections = _.cloneDeep(form.sections);
            if (_modal.parent === 'field') {
                _sections[_modal.sectionIndex].fields[_modal.fieldIndex][_modal.field] = value;
            } else {
                _sections[_modal.sectionIndex][_modal.field] = value;
            }
            setField('sections', _sections);
        }
        setModal(null);
    }

    return (
        <Modal open={!!modal} onClose={() => setModal(null)} >
            <Card sx={{ ...modalStyle, width: '560px'}}>
                <CardContent>
                    {!_modal.parent && (
                        <Typography gutterBottom variant='h5' component='div'>
                            Set this form's <b>{_modal.field}</b>
                        </Typography>
                    )}
                    {_modal.parent === 'section' && (
                        <Typography gutterBottom variant='h5' component='div'>
                            Set section <b>{_modal.field}</b>
                        </Typography>
                    )}
                    <TextField
                        id='modalField'
                        label={!_modal.parent ? `Form's ${_modal.field}` : `${_.startCase(_modal.parent)}'s ${_modal.field}`}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        fullWidth
                        sx={{ marginTop: '12px' }}
                    />
                </CardContent>
                <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button size='small' color='secondary' onClick={() => { setValue(_modal.previous); setModal(null) }}>
                        Cancel
                    </Button>
                    <Button size='small' color='primary' variant='contained' onClick={() => save()}>
                        OK
                    </Button>
                </CardActions>
            </Card>
        </Modal>
    );
}

function ReorderModal(props) {
    const { modal, setModal, onSave } = props;
    const [ value, setValue ] = useState([]);

    useEffect(() => {
        setValue(modal ? _.cloneDeep(modal.options) : []);
    }, [modal])

    function onUp(i) {
        const v = _.cloneDeep(value);
        const [o] = v.splice(i, 1);
        v.splice(i - 1, 0, o);
        setValue(v);
    }

    function onDown(i) {
        const v = _.cloneDeep(value);
        const [o] = v.splice(i, 1);
        v.splice(i + 1, 0, o);
        setValue(v);
    }

    function save() {
        onSave(value);
        setModal(null);
    }

    return (
        <Modal open={!!modal} onClose={() => setModal(null)} >
            <Card sx={{ ...modalStyle, width: '560px'}}>
                <CardContent>
                    <Typography gutterBottom variant='h5' component='div'>
                        Reordering
                    </Typography>
                    <Typography gutterBottom variant='caption' component='div'>
                        Use the buttons to reorder the list.
                    </Typography>
                    { value.map((v, i) => (
                        <Paper key={Math.random()} sx={{ marginTop: '6px', padding: '12px', border: '1px solid #eee' }} elevation={0} >
                            <Box sx={{ display: 'flex' }}>
                                <Typography variant='body1' sx={{ flex: 1 }}>{v.title}</Typography>
                                <IconButton sx={{ flex: 0 }} disabled={i === 0} onClick={() => onUp(i)}><FontAwesomeIcon icon={faAngleUp}/></IconButton>
                                <IconButton sx={{ flex: 0 }} disabled={i === value.length - 1} onClick={() => onDown(i)}><FontAwesomeIcon icon={faAngleDown}/></IconButton>
                            </Box>
                        </Paper>
                    ))}
                </CardContent>
                <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button size='small' color='secondary' onClick={() => { setModal(null) }}>
                        Cancel
                    </Button>
                    <Button size='small' color='primary' variant='contained' onClick={() => save()} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Save
                    </Button>
                </CardActions>
            </Card>
        </Modal>
    );
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

// Rank supplier matches so names that START with the query appear first,
// then any names that merely contain the query (each group kept A-Z).
function filterSuppliers(options, { inputValue }) {
    const q = (inputValue || '').trim().toLowerCase();
    if (!q) return options;
    const matches = options.filter(o => o.toLowerCase().includes(q));
    return _.sortBy(matches, o => (o.toLowerCase().startsWith(q) ? 0 : 1));
}

function parseFloatOrString(s, divider = 1) {
    if (s.match(/^[0-9]+\.0+$/)) {
        if (divider === 1) return s;
        return parseFloat(s) / divider;
    }
    if (s.match(/^[0-9]+\.[0-9]+$/)) return parseFloat(s) / divider;
    if (s.match(/^[0-9]+$/)) return parseFloat(s) / divider;
    return s;
}
